import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { cn } from "@/shared/lib/utils";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../primitives/form";
import { Textarea } from "../primitives/textarea";

type ColocaTextareaFieldProps = {
  /** Field name as registered in the parent `useForm` schema. Must match the Zod key exactly. */
  name: string;
  /** Visible label rendered above the textarea. */
  label?: ReactNode;
  /**
   * Helper text rendered below the textarea. Use for format hints or length constraints. Do not repeat the
   * label.
   */
  description?: ReactNode;
  placeholder?: string;
  /** Number of visible text rows. The textarea auto-grows beyond this via `field-sizing-content`. */
  rows?: number;
  /**
   * Maximum number of characters. Enforced by the browser and displayed in the counter when `count` is true.
   * Pair with a Zod `max()` validator to show an error when exceeded programmatically.
   */
  maxLength?: number;
  /** When true, shows a live character counter aligned to the right below the textarea. */
  count?: boolean;
  disabled?: boolean;
};

/**
 * Controlled textarea wired to the nearest React Hook Form context.
 *
 * Use inside a `<ColocaForm>` for any multi-line text field — e.g. notes, messages, descriptions. The
 * textarea grows automatically with content via `field-sizing-content`. Validation errors from the Zod schema
 * are displayed automatically below the input via `<FormMessage>`.
 *
 * For single-line text input use `ColocaInputField`.
 */
export function ColocaTextareaField({
  name,
  label,
  description,
  placeholder,
  rows,
  maxLength,
  count,
  disabled,
}: ColocaTextareaFieldProps) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const charCount = (field.value as string)?.length ?? 0;
        const isAtLimit = maxLength !== undefined && charCount >= maxLength;

        return (
          <FormItem>
            {label && (
              <FormLabel>
                {label}{" "}
                {count && (
                  <p
                    className={cn(
                      "ml-auto shrink-0 text-xs tabular-nums",
                      isAtLimit ? "text-destructive" : "text-muted-foreground",
                    )}
                  >
                    {maxLength ? `${charCount} / ${maxLength}` : charCount}
                  </p>
                )}
              </FormLabel>
            )}
            <FormControl>
              <Textarea
                placeholder={placeholder}
                rows={rows}
                maxLength={maxLength}
                disabled={disabled}
                {...field}
              />
            </FormControl>
            {description && (
              <div className="flex items-start gap-2">
                {description && <FormDescription className="flex-1">{description}</FormDescription>}
              </div>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
